import React from "react";
import { ColDef } from "ag-grid-community";
import Asset from "../../../models/asset";
import DocumentCategory from "../../../models/document-category";
import DocumentSubCategory from "../../../models/document-subcategory";
import Subcompany from "../../../models/subcompany";

interface BuildRelationshipColumnsParams {
  assets: Asset[];
  changedCells: { [key: number]: Set<string> };
  documentCategoryId: DocumentCategory[];
  documentSubcategoryId: DocumentSubCategory[];
  markCellChanged: (rowId: number, field?: string) => void;
  subcompany: Subcompany[];
}

const selectStyle = {
  width: "100%",
  padding: "5px",
  border: "1px solid #ccc",
  borderRadius: "4px",
};

const changedCellRule = (changedCells: { [key: number]: Set<string> }) => ({
  "cell-changed": (params: any) =>
    params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
});

export const buildRelationshipColumns = ({
  assets,
  changedCells,
  documentCategoryId,
  documentSubcategoryId,
  markCellChanged,
  subcompany,
}: BuildRelationshipColumnsParams): ColDef[] => [
  {
    field: "subcompanyId",
    headerName: "Sub Organización",
    filter: "agTextColumnFilter",
    filterValueGetter: (params) => {
      if (!params.data) return "";
      const subId = params.data.subcompanyId;
      const sub = subcompany.find((s) => s.id === subId);
      return sub?.name || "";
    },
    filterParams: {
      textFormatter: (searchTerm: string) => {
        if (!searchTerm) return "";
        return searchTerm.toLowerCase();
      },
    },
    cellClassRules: changedCellRule(changedCells),
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedValue = parseInt(e.target.value, 10);
        params.setValue(updatedValue);
        markCellChanged(params.data.id, "subcompanyId");
      };

      return (
        <select
          value={params.value || ""}
          onChange={handleChange}
          style={selectStyle}
        >
          <option value="" disabled>
            Seleccione una suborganización
          </option>
          {subcompany.map((sub: Subcompany) => (
            <option key={sub.id} value={sub.id}>
              {sub.name || `Suborganización ${sub.id}`}
            </option>
          ))}
        </select>
      );
    },
  },
  {
    field: "documentCategoryId",
    headerName: "Categoría de Documento",
    filter: "agTextColumnFilter",
    filterValueGetter: (params) => {
      if (!params.data) return "";
      const categoryId = params.data.documentCategoryId;
      const category = documentCategoryId.find((c) => c.id === categoryId);
      return category?.name || "";
    },
    filterParams: {
      textFormatter: (searchTerm: string) => searchTerm.toLowerCase(),
    },
    cellClassRules: changedCellRule(changedCells),
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCategoryId = parseInt(e.target.value, 10);
        if (newCategoryId === params.value) {
          return;
        }

        params.setValue(newCategoryId);
        params.node.setDataValue("documentSubcategoryId", null);
        markCellChanged(params.data.id, "documentCategoryId");
        markCellChanged(params.data.id, "documentSubcategoryId");
      };

      return (
        <select
          value={params.value || ""}
          onChange={handleChange}
          style={selectStyle}
        >
          <option value="" disabled>
            Seleccione categoría
          </option>
          {documentCategoryId.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      );
    },
  },
  {
    field: "documentSubcategoryId",
    headerName: "Subcategoría de Documento",
    filter: "agTextColumnFilter",
    filterValueGetter: (params) => {
      if (!params.data) return "";
      const subcategoryId = params.data.documentSubcategoryId;
      const subcategory = documentSubcategoryId.find(
        (s) => s.id === subcategoryId,
      );
      return subcategory?.name || "";
    },
    filterParams: {
      textFormatter: (searchTerm: string) => searchTerm.toLowerCase(),
    },
    cellClassRules: changedCellRule(changedCells),
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const rowData = params.data;
      const filteredSubcategories = documentSubcategoryId.filter(
        (subcat) => subcat.categoryId === rowData.documentCategoryId,
      );
      const isValidSubcategory = filteredSubcategories.some(
        (subcat) => subcat.id === rowData.documentSubcategoryId,
      );

      const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value ? parseInt(e.target.value, 10) : null;
        params.node.setDataValue("documentSubcategoryId", newValue);
        markCellChanged(rowData.id, "documentSubcategoryId");
      };

      return (
        <select
          value={rowData.documentSubcategoryId || ""}
          onChange={handleChange}
          disabled={!rowData.documentCategoryId}
          style={selectStyle}
        >
          <option value="" disabled>
            {rowData.documentCategoryId
              ? "Seleccione subcategoría"
              : "Seleccione categoría primero"}
          </option>
          {filteredSubcategories.map((subcat) => (
            <option key={subcat.id} value={subcat.id}>
              {subcat.name}
            </option>
          ))}
          {rowData.documentSubcategoryId && !isValidSubcategory && (
            <option value={rowData.documentSubcategoryId} disabled>
              {documentSubcategoryId.find(
                (sc) => sc.id === rowData.documentSubcategoryId,
              )?.name || `Subcategoría ${rowData.documentSubcategoryId}`}
            </option>
          )}
        </select>
      );
    },
  },
  {
    field: "assetId",
    headerName: "Activo",
    filter: "agTextColumnFilter",
    filterValueGetter: (params) => {
      if (!params.data) return "";
      const assetId = params.data.assetId;
      const asset = assets.find((a) => a.id === assetId);
      return asset ? asset.name : "";
    },
    cellClassRules: changedCellRule(changedCells),
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedValue = e.target.value ? parseInt(e.target.value, 10) : null;
        const selectedAsset = assets.find((a) => a.id === updatedValue);

        params.node.setDataValue("assetId", updatedValue);
        params.node.setDataValue("typeName", selectedAsset?.typeName || "");
        markCellChanged(params.data.id, "assetId");
        markCellChanged(params.data.id, "typeName");
      };

      return (
        <select
          value={params.value || ""}
          onChange={handleChange}
          style={selectStyle}
        >
          <option value="" disabled>
            Seleccione un Activo
          </option>
          {assets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.name}
            </option>
          ))}
        </select>
      );
    },
  },
];
